// MENU_QUALITY_PLAN Phase A4 + Phase H4 — admin sweep for menu_items
// still at item_category='unknown' OR item_subcategory='unknown'.
//
// Mode tabs at the top switch which column the sweep is targeting.
// Coarse mode → 4 buttons per row (food/beverage/dessert/side); the
// server auto-derives a sensible subcategory when one isn't set.
// Subcategory mode → 8 buttons per row (soup/main/salad/side/dessert/
// coffee_tea/cold_drink/alcoholic); the server auto-derives the coarse
// from SUB_TO_CAT so the two columns stay consistent.
//
// Layout: left column shows the "top groups" — (restaurant, section)
// pairs with the highest unknown counts (for the active mode). Clicking
// a group filters the right table. Right column shows individual items
// with per-row category/subcategory pickers AND a "Set all N visible →
// X" header when a group filter is active.

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { AdminShell } from '@/components/AdminShell';
import {
  deleteMenuItem,
  listUnknownMenuItems,
  setMenuItemCategory,
  type ListUnknownItemsResponse,
  type SetCategoryBody,
  type UnknownItemGroup,
  type UnknownItemRow,
  type UnknownMode,
  type ItemCategory,
  type ItemSubcategory,
} from '@/api/admin';
import { ApiError } from '@/api/client';

const PAGE_SIZE = 100;

const CATEGORIES: ItemCategory[] = ['food', 'beverage', 'dessert', 'side'];
const SUBCATEGORIES: ItemSubcategory[] = [
  'soup',
  'appetizer',
  'main',
  'salad',
  'side',
  'dessert',
  'coffee_tea',
  'cold_drink',
  'alcoholic',
];

interface Filter {
  restaurantId?: number;
  sectionFilter?: string;
}

export function MenuItemsCleanupPage() {
  const [mode, setMode] = useState<UnknownMode>('category');
  const [filter, setFilter] = useState<Filter>({});
  const [page, setPage] = useState(0);
  const queryClient = useQueryClient();

  const apiParams = {
    mode,
    restaurant_id: filter.restaurantId,
    section_filter: filter.sectionFilter,
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
  };

  const query = useQuery<ListUnknownItemsResponse, ApiError>({
    queryKey: ['admin', 'menu-items-unknown', apiParams],
    queryFn: ({ signal }) => listUnknownMenuItems(apiParams, signal),
    placeholderData: keepPreviousData,
  });

  const mutation = useMutation({
    mutationFn: (body: SetCategoryBody) => setMenuItemCategory(body),
    onSuccess: () => {
      // Refresh both the list and the group counts (which depend on the
      // overall unknown count, not just the filtered page).
      void queryClient.invalidateQueries({
        queryKey: ['admin', 'menu-items-unknown'],
      });
    },
  });

  // Separate mutation for deletes so its pending state doesn't blank
  // out the assignment buttons (and vice versa). Same invalidate so
  // the row drops out of the list once gone.
  const deleteMutation = useMutation({
    mutationFn: (id: number) => deleteMenuItem(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['admin', 'menu-items-unknown'],
      });
    },
  });

  function changeMode(next: UnknownMode) {
    if (next === mode) return;
    setMode(next);
    setPage(0);
    setFilter({});
  }

  function selectGroup(g: UnknownItemGroup) {
    setPage(0);
    setFilter({
      restaurantId: g.restaurant_id,
      sectionFilter: g.section ?? undefined,
    });
  }

  function clearFilter() {
    setPage(0);
    setFilter({});
  }

  function assignOne(id: number, value: ItemCategory | ItemSubcategory) {
    if (mode === 'category') {
      mutation.mutate({ ids: [id], category: value as ItemCategory });
    } else {
      mutation.mutate({ ids: [id], subcategory: value as ItemSubcategory });
    }
  }

  function deleteOne(row: UnknownItemRow) {
    // window.confirm is the lightest-weight guard available — it's
    // a HARD delete, so even a one-misclick safeguard is worth the
    // mild UX friction. Title + restaurant name in the message so the
    // founder doesn't accidentally nuke the wrong row when the table
    // is dense.
    const ok = window.confirm(
      `Delete "${row.title}" from ${row.restaurant_name}?\n\nThis cannot be undone from the UI (the row is recoverable only by re-importing the original menu).`,
    );
    if (!ok) return;
    deleteMutation.mutate(row.id);
  }

  function assignAllVisible(value: ItemCategory | ItemSubcategory) {
    const ids = (query.data?.rows ?? []).map((r) => r.id);
    if (ids.length === 0) return;
    if (mode === 'category') {
      mutation.mutate({ ids, category: value as ItemCategory });
    } else {
      mutation.mutate({ ids, subcategory: value as ItemSubcategory });
    }
  }

  const totalPages = query.data
    ? Math.max(1, Math.ceil(query.data.total / PAGE_SIZE))
    : 1;

  const choices = mode === 'category' ? CATEGORIES : SUBCATEGORIES;

  return (
    <AdminShell>
      <header className="mb-6">
        <h1 className="text-2xl font-semibold tracking-tight">
          Menu items · category cleanup
        </h1>
        <div className="mt-2 flex items-center gap-2 text-sm">
          <ModeTab active={mode === 'category'} onClick={() => changeMode('category')}>
            Coarse · category
          </ModeTab>
          <ModeTab active={mode === 'subcategory'} onClick={() => changeMode('subcategory')}>
            Fine · subcategory
          </ModeTab>
        </div>
        <p className="mt-2 text-sm text-slate-500">
          {query.data
            ? `${query.data.total.toLocaleString()} unknown ${query.data.total === 1 ? 'item' : 'items'}`
            : 'Loading…'}
          {filter.restaurantId !== undefined || filter.sectionFilter ? (
            <>
              {' · '}
              <button
                type="button"
                className="text-accent underline underline-offset-2"
                onClick={clearFilter}
              >
                Clear filter
              </button>
            </>
          ) : null}
        </p>
      </header>

      {query.isError && (
        <div className="card p-4 mb-4 border-rose-200 bg-rose-50 text-rose-900">
          Failed to load: {query.error.message}
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-[320px_1fr]">
        <Groups
          groups={query.data?.groups ?? []}
          activeRestaurantId={filter.restaurantId}
          activeSection={filter.sectionFilter}
          onSelect={selectGroup}
        />

        <section>
          {(filter.restaurantId !== undefined || filter.sectionFilter) && (
            <div className="card p-3 mb-3 flex flex-wrap items-center gap-3">
              <span className="text-sm text-slate-700">
                Set all {query.data?.rows.length ?? 0} visible →
              </span>
              {choices.map((value) => (
                <button
                  key={value}
                  type="button"
                  className="btn btn-secondary text-xs"
                  disabled={mutation.isPending || (query.data?.rows.length ?? 0) === 0}
                  onClick={() => assignAllVisible(value)}
                >
                  {value}
                </button>
              ))}
            </div>
          )}

          <ItemsTable
            mode={mode}
            choices={choices}
            rows={query.data?.rows ?? []}
            loading={query.isLoading || query.isPlaceholderData}
            disabled={mutation.isPending || deleteMutation.isPending}
            onAssign={assignOne}
            onDelete={deleteOne}
          />

          {query.data && totalPages > 1 && (
            <div className="mt-4 flex items-center justify-between text-sm">
              <button
                type="button"
                className="btn btn-secondary"
                disabled={page <= 0}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
              >
                ← Previous
              </button>
              <span className="text-slate-600">
                Page {page + 1} of {totalPages}
              </span>
              <button
                type="button"
                className="btn btn-secondary"
                disabled={page + 1 >= totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                Next →
              </button>
            </div>
          )}
        </section>
      </div>
    </AdminShell>
  );
}

// ---------------------------------------------------------------------------

function ModeTab({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        active
          ? 'px-3 py-1 rounded-full bg-accent text-white text-xs font-medium'
          : 'px-3 py-1 rounded-full border border-slate-300 text-slate-600 text-xs hover:bg-slate-50'
      }
    >
      {children}
    </button>
  );
}

function Groups({
  groups,
  activeRestaurantId,
  activeSection,
  onSelect,
}: {
  groups: UnknownItemGroup[];
  activeRestaurantId?: number;
  activeSection?: string;
  onSelect: (g: UnknownItemGroup) => void;
}) {
  if (groups.length === 0) {
    return (
      <aside className="card p-4">
        <p className="text-sm text-slate-500">No unknown items left. 🎉</p>
      </aside>
    );
  }

  return (
    <aside className="card p-3">
      <p className="px-2 pt-1 pb-2 text-xs font-medium uppercase tracking-wider text-slate-500">
        Top unknown groups
      </p>
      <ul className="flex flex-col gap-1">
        {groups.map((g) => {
          const isActive =
            g.restaurant_id === activeRestaurantId &&
            (g.section ?? undefined) === activeSection;
          return (
            <li key={`${g.restaurant_id}|${g.section ?? ''}`}>
              <button
                type="button"
                onClick={() => onSelect(g)}
                className={
                  isActive
                    ? 'w-full text-left rounded px-2 py-1.5 bg-accent/10 text-accent'
                    : 'w-full text-left rounded px-2 py-1.5 hover:bg-slate-100'
                }
              >
                <div className="text-sm text-slate-900 truncate">
                  {g.restaurant_name}
                </div>
                <div className="text-xs text-slate-500 flex items-center justify-between gap-2">
                  <span className="truncate">
                    {g.section ?? <em className="text-slate-400">(no section)</em>}
                  </span>
                  <span className="font-mono">{g.n}</span>
                </div>
              </button>
            </li>
          );
        })}
      </ul>
    </aside>
  );
}

function ItemsTable({
  mode,
  choices,
  rows,
  loading,
  disabled,
  onAssign,
  onDelete,
}: {
  mode: UnknownMode;
  choices: readonly (ItemCategory | ItemSubcategory)[];
  rows: UnknownItemRow[];
  loading: boolean;
  disabled: boolean;
  onAssign: (id: number, value: ItemCategory | ItemSubcategory) => void;
  onDelete: (row: UnknownItemRow) => void;
}) {
  if (rows.length === 0) {
    return (
      <div className="card p-8 text-center text-sm text-slate-500">
        {loading ? 'Loading…' : 'No items match the current filter.'}
      </div>
    );
  }

  // Column showing the OTHER classification — in coarse mode we show
  // the row's existing subcategory (for context); in fine mode we show
  // the coarse. Helps the operator decide ("oh this row is already
  // coarse=beverage, picking 'alcoholic' is consistent").
  const otherLabel = mode === 'category' ? 'subcategory' : 'category';
  const otherKey: keyof UnknownItemRow =
    mode === 'category' ? 'item_subcategory' : 'item_category';

  return (
    <div className="card overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
          <tr>
            <th className="text-left px-3 py-2 font-medium">Restaurant</th>
            <th className="text-left px-3 py-2 font-medium">Section</th>
            <th className="text-left px-3 py-2 font-medium">Item</th>
            <th className="text-left px-3 py-2 font-medium">{otherLabel}</th>
            <th className="text-left px-3 py-2 font-medium">Set {mode}</th>
            <th className="text-right px-3 py-2 font-medium">Delete</th>
          </tr>
        </thead>
        <tbody className={loading ? 'opacity-60' : ''}>
          {rows.map((r) => (
            <tr key={r.id} className="border-t border-slate-200">
              <td className="px-3 py-2 align-top text-slate-700 max-w-[180px] truncate">
                {r.restaurant_name}
              </td>
              <td className="px-3 py-2 align-top text-slate-500 max-w-[180px] truncate">
                {r.section ?? <em className="text-slate-400">(none)</em>}
              </td>
              <td className="px-3 py-2 align-top text-slate-900">{r.title}</td>
              <td className="px-3 py-2 align-top text-slate-500 text-xs font-mono">
                {r[otherKey]}
              </td>
              <td className="px-3 py-2 align-top">
                <div className="flex items-center gap-1.5 flex-wrap">
                  {choices.map((value) => (
                    <button
                      key={value}
                      type="button"
                      className="btn btn-secondary text-xs"
                      disabled={disabled}
                      onClick={() => onAssign(r.id, value)}
                    >
                      {value}
                    </button>
                  ))}
                </div>
              </td>
              <td className="px-3 py-2 align-top text-right">
                <button
                  type="button"
                  className="text-xs text-rose-600 hover:text-rose-800 underline underline-offset-2 disabled:text-slate-400 disabled:no-underline"
                  disabled={disabled}
                  onClick={() => onDelete(r)}
                  title="Delete this menu item (for combos / section headers / other meta-rows the scanner mis-ingested)"
                >
                  Delete
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
